/**
 * API Service - Handles all backend communication
 */

// Use environment variable for API URL
// In development: Use empty string for relative URLs (goes through setupProxy.js)
// In production: Use empty string for relative URLs (served from same origin)
// For direct backend access: Set REACT_APP_API_URL=http://localhost:2800
const API_BASE_URL = process.env.REACT_APP_API_URL || '';  // Default: relative URLs

console.log('API Service initialized with base URL:', API_BASE_URL || '(relative URLs - via proxy)');

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const { method = 'GET', headers = {}, ...otherOptions } = options;

    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      // Prevent browser caching of GET responses to ensure fresh data after deletions
      cache: 'no-store',
      ...otherOptions
    };

    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);

      // Handle 204 No Content responses (no body expected)
      let data;
      if (response.status === 204) {
        data = null; // 204 responses have no body
      } else {
        // Get the response text first, then try to parse as JSON
        const responseText = await response.text();

        // Try to parse as JSON if there's content
        if (responseText.trim()) {
          try {
            data = JSON.parse(responseText);
          } catch (jsonError) {
            console.error(`Non-JSON response from ${url}:`, responseText);
            throw new Error(`Server returned non-JSON response: ${responseText}`);
          }
        } else {
          data = null; // Empty response body
        }
      }

      console.log(`API response from ${url}:`, { status: response.status, data });

      if (!response.ok) {
        // Handle different error response formats
        // Throw an object that preserves the API error structure
        const errorObject = {
          status: response.status,
          detail: data?.detail || `HTTP error! status: ${response.status}`,
          message: data?.message,
          originalData: data
        };

        console.error(`==== API ERROR ====`);
        console.error(`Status: ${response.status}`);
        console.error(`Detail:`, errorObject.detail);
        console.error(`Full error object:`, errorObject);
        
        // Create error that preserves detail for formatApiError
        const error = new Error();
        error.detail = errorObject.detail;
        error.message = typeof errorObject.detail === 'string' 
          ? errorObject.detail 
          : JSON.stringify(errorObject.detail);
        error.status = errorObject.status;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('==== API REQUEST FAILED ====');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }

  // Authentication methods
  async registerStep1(data) {
    const response = await this.request('/api/auth/register/step1', {
      method: 'POST',
      body: JSON.stringify(data)
    });

    // Store token for subsequent requests
    if (response.access_token) {
      localStorage.setItem('authToken', response.access_token);
    }

    return response;
  }

  async registerStep2(data) {
    return await this.request('/api/auth/register/step2', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async registerStep4(data) {
    return await this.request('/api/auth/register/step4', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async registerStep5(data) {
    return await this.request('/api/auth/register/step5', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async login(email, password) {
    const response = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    // Store token
    if (response.access_token) {
      localStorage.setItem('authToken', response.access_token);
    }

    return response;
  }

  async getCurrentUser() {
    return await this.request('/api/auth/me');
  }

  async getFamilyInfo() {
    return await this.request('/api/auth/family');
  }

  async updateParentInfo(parentId, parentData) {
    return await this.request(`/api/auth/parent/${parentId}`, {
      method: 'PUT',
      body: JSON.stringify(parentData)
    });
  }

  async updatePlayerInfo(playerId, playerData) {
    return await this.request(`/api/auth/player/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(playerData)
    });
  }

  async addParent(parentData) {
    return await this.request('/api/auth/parent', {
      method: 'POST',
      body: JSON.stringify(parentData)
    });
  }

  async addPlayer(playerData) {
    return await this.request('/api/auth/player', {
      method: 'POST',
      body: JSON.stringify(playerData)
    });
  }

  async getRegistrationProgress() {
    return await this.request('/api/auth/registration/progress');
  }

  async getInboxTasks() {
    return await this.request('/api/users/me/inbox');
  }

  async getInboxTasksFiltered(statusFilter = null) {
    let url = '/api/users/me/inbox';
    if (statusFilter) {
      url += `?status_filter=${statusFilter}`;
    }
    return await this.request(url);
  }

  async completeInboxTask(taskId) {
    return await this.request(`/api/users/me/inbox/${taskId}/complete`, {
      method: 'POST'
    });
  }

  async dismissInboxTask(taskId) {
    return await this.request(`/api/users/me/inbox/${taskId}/dismiss`, {
      method: 'POST'
    });
  }

  async saveAndExit(step, data) {
    return await this.request('/api/auth/registration/save-and-exit', {
      method: 'POST',
      body: JSON.stringify({ step, data })
    });
  }

  // Utility methods
  logout() {
    localStorage.removeItem('authToken');
  }

  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  }

  async healthCheck() {
    return await this.request('/health');
  }

  // Skills API methods
  async getPlayerSkills(playerId) {
    return await this.request(`/api/skills/player/${playerId}`);
  }

  async updatePlayerSkills(playerId, skillsData) {
    return await this.request(`/api/skills/player/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(skillsData)
    });
  }

  async getSkills() {
    return await this.request('/api/skills');
  }

  // =====================================================
  // Public endpoints (no authentication required)
  // =====================================================

  async getTeamMembers(roles = null) {
    let url = '/api/team-members';
    if (roles && roles.length > 0) {
      url += `?roles=${roles.join(',')}`;
    }
    return await this.request(url);
  }

  // Waiver functionality removed - now managed through documents system

  async downloadDocumentPDF(filename) {
    const url = `${this.baseURL}/api/document-requirements/download/${filename}`;
    const token = localStorage.getItem('authToken');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to download document PDF: ${errorText}`);
    }

    return response.blob();
  }

  async viewDocumentPDF(filename) {
    const url = `${this.baseURL}/api/document-requirements/view/${filename}`;
    const token = localStorage.getItem('authToken');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to view document PDF: ${errorText}`);
    }

    return response.blob();
  }

  // =====================================================
  // Club Hierarchy Management API (Platform Admin)
  // =====================================================

  /**
   * Create a new club (platform admin only)
   * Uses hierarchical endpoint: POST /api/admin/clubs
   */
  async createClub(clubData) {
    return await this.request('/api/admin/clubs', {
      method: 'POST',
      body: JSON.stringify(clubData)
    });
  }

  /**
   * List all clubs (platform admin only)
   * Uses hierarchical endpoint: GET /api/admin/clubs
   */
  async getClubs(filters = {}) {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/api/admin/clubs?${queryString}` : '/api/admin/clubs';

    return await this.request(endpoint);
  }

  /**
   * Get club by ID (platform admin only)
   * Uses hierarchical endpoint: GET /api/admin/clubs/{club_id}
   */
  async getClub(club_id) {
    return await this.request(`/api/admin/clubs/${club_id}`);
  }

  async getClubDomains(club_id, includeStatus = true) {
    const params = new URLSearchParams({ include_status: includeStatus });
    return await this.request(`/api/clubs/${club_id}/domains?${params}`);
  }

  /**
   * Update club (platform admin only)
   * Uses hierarchical endpoint: PATCH /api/admin/clubs/{club_id}
   */
  async updateClub(club_id, clubData) {
    return await this.request(`/api/admin/clubs/${club_id}`, {
      method: 'PATCH',
      body: JSON.stringify(clubData)
    });
  }

  /**
   * Delete/deactivate club (platform admin only)
   * Uses hierarchical endpoint: DELETE /api/admin/clubs/{club_id}
   */
  async deactivateClub(club_id, reason) {
    return await this.request(`/api/admin/clubs/${club_id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Reactivate club (platform admin only)
   */
  async reactivateClub(club_id) {
    return await this.request(`/api/admin/clubs/${club_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'active' })
    });
  }

  /**
   * Delete club (platform admin only)
   * Hard delete: DELETE /api/clubs/{club_id}
   */
  async deleteClub(club_id) {
    return await this.request(`/api/clubs/${club_id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get club statistics (platform admin only)
   * Uses hierarchical endpoint: GET /api/admin/clubs/{club_id}/stats
   */
  async getClubStats(club_id) {
    return await this.request(`/api/admin/clubs/${club_id}/stats`);
  }

  // =====================================================
  // Club Branch Management API
  // =====================================================

  /**
   * Create a branch under a club (club admin or platform admin)
   * Uses hierarchical endpoint: POST /api/clubs/branches (tenant context) or POST /api/clubs/{club_id}/branches (platform admin)
   */
  async createBranch(club_id, locationData) {
    // If club_id provided, use club-specific endpoint (for platform admin)
    // Otherwise use tenant-context endpoint (for club admin)
    if (club_id) {
      return await this.request(`/api/clubs/${club_id}/branches`, {
        method: 'POST',
        body: JSON.stringify(locationData)
      });
    }

    return await this.request('/api/clubs/branches', {
      method: 'POST',
      body: JSON.stringify(locationData)
    });
  }

  /**
   * Get potential branch admins for current club
   * Returns list of users who can be assigned as branch administrators
   */
  async getPotentialBranchAdmins() {
    return await this.request('/api/clubs/branches/potential-admins');
  }

  /**
   * List branches for a club (club admin or platform admin)
   * Uses hierarchical endpoint: GET /api/clubs/{club_id}/branches
   */
  async getClubBranches(club_id, filters = {}) {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.location_type) params.append('location_type', filters.location_type);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();

    // If club_id provided (for platform admin viewing specific club), use club-specific endpoint
    // Otherwise use tenant-context endpoint (for club admin viewing their own club)
    if (club_id) {
      const endpoint = queryString
        ? `/api/clubs/${club_id}/branches?${queryString}`
        : `/api/clubs/${club_id}/branches`;
      return await this.request(endpoint);
    }

    const endpoint = queryString
      ? `/api/clubs/branches?${queryString}`
      : `/api/clubs/branches`;
    return await this.request(endpoint);
  }

  /**
   * Get branch by ID
   * Uses hierarchical endpoint: GET /api/branches/{branch_id}
   */
  async getBranch(branch_id) {
    return await this.request(`/api/branches/${branch_id}`);
  }

  /**
   * Update branch
   * Uses hierarchical endpoint: PATCH /api/branches/{branch_id}
   */
  async updateBranch(branch_id, locationData) {
    return await this.request(`/api/branches/${branch_id}`, {
      method: 'PATCH',
      body: JSON.stringify(locationData)
    });
  }

  /**
   * Delete/deactivate branch
   * Uses hierarchical endpoint: DELETE /api/branches/{branch_id}
   */
  async deleteBranch(branch_id) {
    return await this.request(`/api/branches/${branch_id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Deactivate branch (sets status to inactive)
   */
  async deactivateBranch(branch_id) {
    return await this.request(`/api/branches/${branch_id}/deactivate`, {
      method: 'POST'
    });
  }

  /**
   * Activate/reactivate branch (sets status to active)
   */
  async activateBranch(branch_id) {
    return await this.request(`/api/branches/${branch_id}/activate`, {
      method: 'POST'
    });
  }

  /**
   * Assign or create a branch administrator
   * Uses hierarchical endpoint: POST /api/branches/{branch_id}/admin
   */
  async assignBranchAdmin(branch_id, adminData) {
    return await this.request(`/api/branches/${branch_id}/admin`, {
      method: 'POST',
      body: JSON.stringify(adminData)
    });
  }

  /**
   * Get branch statistics
   * Uses hierarchical endpoint: GET /api/branches/{branch_id}/stats
   */
  async getBranchStats(branch_id) {
    return await this.request(`/api/branches/${branch_id}/stats`);
  }

  /**
   * Get branch configuration (with inherited club settings)
   * Uses hierarchical endpoint: GET /api/branches/{branch_id}/config
   */
  async getBranchConfig(branch_id) {
    return await this.request(`/api/branches/${branch_id}/config`);
  }

  // =====================================================
  // Club Settings (Legacy - may need tenant context)
  // =====================================================

  async getClubSettings(club_id) {
    // If club_id provided (for platform admin), use admin endpoint
    // Otherwise use tenant-context endpoint (for club admin)
    if (club_id) {
      return await this.request(`/api/admin/clubs/${club_id}/settings`);
    }
    return await this.request('/api/clubs/settings');
  }

  async updateClubSettings(club_id, settingsData) {
    // If club_id provided (for platform admin), use admin endpoint
    // Otherwise use tenant-context endpoint (for club admin)
    if (club_id) {
      return await this.request(`/api/admin/clubs/${club_id}/settings`, {
        method: 'PUT',
        body: JSON.stringify(settingsData)
      });
    }
    return await this.request('/api/clubs/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  // Club User Management API methods (Club Admin)
  async getClubUsers() {
    return await this.request('/api/clubs/users');
  }

  async createClubUser(userData) {
    return await this.request('/api/clubs/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateClubUser(userId, userData) {
    return await this.request(`/api/clubs/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deactivateUser(userId, reason) {
    return await this.request(`/api/admin/users/${userId}/deactivate`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }

  async reactivateUser(userId) {
    return await this.request(`/api/admin/users/${userId}/reactivate`, {
      method: 'POST'
    });
  }

  async getUser(userId) {
    return await this.request(`/api/admin/users/${userId}`);
  }

  async updateUser(userId, userData) {
    // Ensure we send canonical primary_role_id when a role is provided.
    // Also include primary_role for backward compatibility (many endpoints expect it).
    const payload = { ...userData };
    if (payload.role) {
      // `role` from form is expected to be the role ID (availableRoles[].id). Use it as canonical id.
      // Send only the canonical `primary_role_id`; backend will populate the human-readable `primary_role`.
      payload.primary_role_id = payload.role;
      // Do not send `primary_role` as the id to avoid conflating display names with ids.
    }

    return await this.request(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  async deleteUser(userId) {
    return await this.request(`/api/admin/users/${userId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Upload user photo (multipart/form-data)
   * Uses direct fetch because multipart bodies must not set JSON content-type
   */
  async uploadUserPhoto(userId, formData) {
    const url = `${this.baseURL}/api/admin/users/${userId}/photo`;
    const token = localStorage.getItem('authToken');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    // Try to parse JSON response if present
    const text = await response.text();
    const data = text.trim() ? JSON.parse(text) : null;

    if (!response.ok) {
      const error = new Error(data?.detail || `HTTP error ${response.status}`);
      error.status = response.status;
      error.detail = data?.detail;
      throw error;
    }

    return data;
  }

  async resetUserPassword(userId) {
    return await this.request(`/api/admin/users/${userId}/reset-password`, {
      method: 'POST'
    });
  }

  async verifyUser(userId, verificationComment = '') {
    const body = {};
    if (verificationComment && verificationComment.trim()) {
      body.verification_comment = verificationComment;
    }

    return await this.request(`/api/admin/users/${userId}/verify`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // Role Template Management API methods (Platform Admin)
  async getRoleTemplates(activeOnly = true, page = 1, pageSize = 20) {
    const params = new URLSearchParams();
    params.append('active_only', activeOnly.toString());
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());

    return await this.request(`/api/role-templates/?${params.toString()}`);
  }

  async getRoleTemplate(templateId) {
    return await this.request(`/api/role-templates/${templateId}`);
  }

  async createRoleTemplate(templateData) {
    return await this.request('/api/role-templates/', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
  }

  async updateRoleTemplate(templateId, templateData) {
    return await this.request(`/api/role-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData)
    });
  }

  async deleteRoleTemplate(templateId) {
    return await this.request(`/api/role-templates/${templateId}`, {
      method: 'DELETE'
    });
  }

  async deactivateRoleTemplate(templateId) {
    return await this.request(`/api/role-templates/${templateId}/deactivate`, {
      method: 'POST'
    });
  }

  async activateRoleTemplate(templateId) {
    return await this.request(`/api/role-templates/${templateId}/activate`, {
      method: 'POST'
    });
  }

  async getAvailablePermissions() {
    return await this.request('/api/role-templates/permissions/available');
  }

  async seedDefaultRoleTemplates() {
    return await this.request('/api/role-templates/seed', {
      method: 'POST'
    });
  }

  // Skill Template Methods
  async getSkillTemplates(activeOnly = true, page = 1, pageSize = 20) {
    const params = new URLSearchParams();
    params.append('active_only', activeOnly.toString());
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());

    try {
      return await this.request(`/api/skills/templates?${params.toString()}`);
    } catch (error) {
      // Handle 404 gracefully - return empty array when no templates exist
      if (error.status === 404) {
        return [];
      }
      throw error;
    }
  }

  async getSkillTemplate(templateId) {
    return await this.request(`/api/skills/templates/${templateId}`);
  }

  async createSkillTemplate(templateData) {
    return await this.request('/api/skills/templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
  }

  async updateSkillTemplate(templateId, templateData) {
    return await this.request(`/api/skills/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData)
    });
  }

  async deleteSkillTemplate(templateId) {
    return await this.request(`/api/skills/templates/${templateId}`, {
      method: 'DELETE'
    });
  }

  async copySkillTemplate(templateId) {
    return await this.request(`/api/skills/templates/${templateId}/copy`, {
      method: 'POST'
    });
  }

  async listClubSkills(activeOnly = true, page = 1, pageSize = 50) {
    const params = new URLSearchParams();
    params.append('is_active', activeOnly.toString());
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());

    return await this.request(`/api/skills/club-skills?${params.toString()}`);
  }

  async deleteClubSkill(skillId) {
    return await this.request(`/api/skills/club-skills/${skillId}`, {
      method: 'DELETE'
    });
  }

  // Document Template Management - REMOVED
  // All document template management now uses clubDocumentApi service
  // which provides unified, context-aware endpoints for all scopes (platform/club/branch)

  async getDocumentTemplateStats() {
    // Note: Backend doesn't have stats endpoint yet
    console.warn('getDocumentTemplateStats: Backend endpoint not available');
    return { total_templates: 0, active_templates: 0 };
  }

  // Club Roles Management API methods (Club Admin)
  async getClubRoles(activeOnly = true) {
    const params = new URLSearchParams();
    params.append('active_only', activeOnly.toString());

    return await this.request(`/api/clubs/roles?${params.toString()}`);
  }

  async getClubRole(roleId) {
    return await this.request(`/api/clubs/roles/${roleId}`);
  }

  async createClubRole(roleData) {
    return await this.request('/api/clubs/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    });
  }

  async updateClubRole(roleId, roleData) {
    return await this.request(`/api/clubs/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData)
    });
  }

  async deleteClubRole(roleId) {
    return await this.request(`/api/clubs/roles/${roleId}`, {
      method: 'DELETE'
    });
  }

  async copyRoleTemplates(templateIds) {
    return await this.request('/api/clubs/roles/copy-templates', {
      method: 'POST',
      body: JSON.stringify({ template_ids: templateIds })
    });
  }

  async getClubRoleStats() {
    return await this.request('/api/clubs/roles/stats');
  }

  async deactivateClubRole(roleId) {
    return await this.request(`/api/clubs/roles/${roleId}/deactivate`, {
      method: 'POST'
    });
  }

  async reactivateClubRole(roleId) {
    return await this.request(`/api/clubs/roles/${roleId}/reactivate`, {
      method: 'POST'
    });
  }

  // User Invitation Management API methods (Club Admin or Branch Admin)
  async createUserWithInvitation(userData) {
    // If branch_id is provided, use branch admin endpoint
    if (userData.branch_id) {
      return await this.request(`/api/admin/branches/${userData.branch_id}/users/invite`, {
        method: 'POST',
        body: JSON.stringify({
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          phone: userData.phone || null,
          role_id: userData.role, // Map form field 'role' to API field 'role_id'
          location_id: userData.branch_id, // Pass branch_id as location_id for role assignment
          send_invitation: userData.send_invitation,
          password: userData.password || null,
          custom_message: userData.custom_message || null
        })
      });
    }

    // Otherwise use club admin endpoint
    return await this.request('/api/clubs/users/invite', {
      method: 'POST',
      body: JSON.stringify({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        phone: userData.phone || null,
        role_id: userData.role, // Map form field 'role' to API field 'role_id'
        send_invitation: userData.send_invitation,
        password: userData.password || null,
        custom_message: userData.custom_message || null
      })
    });
  }

  async getPendingInvitations() {
    return await this.request('/api/clubs/users/invitations/pending');
  }

  async resendUserInvitation(userId) {
    return await this.request(`/api/clubs/users/${userId}/resend-invitation`, {
      method: 'POST'
    });
  }

  // Public invitation endpoints (no auth required)
  async getInvitationStatus(token) {
    return await this.request(`/api/clubs/invitations/${token}/status`);
  }

  async acceptInvitation(token, password, confirmPassword) {
    return await this.request(`/api/clubs/invitations/${token}/accept`, {
      method: 'POST',
      body: JSON.stringify({
        token: token,
        password: password,
        confirm_password: confirmPassword
      })
    });
  }

  // Calendar Management API methods (Club Admin)
  async getCalendars(filters = {}) {
    const params = new URLSearchParams();

    if (filters.calendar_scope) params.append('calendar_scope', filters.calendar_scope);
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/api/calendars/?${queryString}` : '/api/calendars/';

    return await this.request(endpoint);
  }

  async getCalendar(calendarId) {
    return await this.request(`/api/calendars/${calendarId}`);
  }

  async createCalendar(calendarData) {
    return await this.request('/api/calendars/', {
      method: 'POST',
      body: JSON.stringify(calendarData)
    });
  }

  async updateCalendar(calendarId, calendarData) {
    return await this.request(`/api/calendars/${calendarId}`, {
      method: 'PUT',
      body: JSON.stringify(calendarData)
    });
  }

  async deleteCalendar(calendarId) {
    return await this.request(`/api/calendars/${calendarId}`, {
      method: 'DELETE'
    });
  }

  async getCalendarTemplates(year) {
    const params = year ? `?year=${year}` : '';
    return await this.request(`/api/calendars/templates/suggestions${params}`);
  }

  async getCalendarStats() {
    return await this.request('/api/calendars/stats');
  }

  async deriveTeamDatesFromCalendar(calendarId, programCategory) {
    const params = new URLSearchParams();
    params.append('program_category', programCategory);

    return await this.request(`/api/calendars/${calendarId}/derive-dates?${params.toString()}`);
  }

  async getSuitableCalendarsForProgram(programCategory) {
    const params = new URLSearchParams();
    params.append('program_category', programCategory);

    return await this.request(`/api/calendars/suitable?${params.toString()}`);
  }

  // Program Management API methods
  async getPrograms(filters = {}) {
    const params = new URLSearchParams();

    if (filters.category) params.append('category', filters.category);

    // Support both single status and array of statuses
    if (filters.program_status) {
      if (Array.isArray(filters.program_status)) {
        filters.program_status.forEach(status => params.append('program_status', status));
      } else {
        params.append('program_status', filters.program_status);
      }
    }

    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/api/programs/?${queryString}` : '/api/programs/';

    return await this.request(endpoint);
  }

  async getProgram(programId) {
    return await this.request(`/api/programs/${programId}`);
  }

  async createProgram(programData) {
    return await this.request('/api/programs/', {
      method: 'POST',
      body: JSON.stringify(programData)
    });
  }

  async updateProgram(programId, programData) {
    return await this.request(`/api/programs/${programId}`, {
      method: 'PUT',
      body: JSON.stringify(programData)
    });
  }

  async deleteProgram(programId) {
    return await this.request(`/api/programs/${programId}`, {
      method: 'DELETE'
    });
  }

  // Team Management API methods
  async getAllTeams(filters = {}) {
    const params = new URLSearchParams();
    if (filters.program_id) params.append('program_id', filters.program_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/api/programs/manage/teams?${queryString}` : '/api/programs/manage/teams';

    return await this.request(endpoint);
  }

  async getProgramsForTeamCreation(filters = {}) {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/api/programs/manage/teams/programs-for-team-creation?${queryString}` : '/api/programs/manage/teams/programs-for-team-creation';

    return await this.request(endpoint);
  }

  async getProgramTeams(programId) {
    return await this.request(`/api/programs/${programId}/teams`);
  }

  async getTeam(teamId) {
    return await this.request(`/api/programs/teams/${teamId}`);
  }

  async createTeam(programId, teamData) {
    // If programId is provided, create team under that program
    // Otherwise, create team at branch/club level
    const endpoint = programId 
      ? `/api/programs/${programId}/teams`
      : `/api/programs/teams`;
    
    return await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(teamData)
    });
  }

  async updateTeam(teamId, teamData) {
    return await this.request(`/api/programs/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(teamData)
    });
  }

  async deleteTeam(teamId) {
    return await this.request(`/api/programs/teams/${teamId}`, {
      method: 'DELETE'
    });
  }

  // Team Tryouts methods
  async getTeamTryouts(teamId) {
    return await this.request(`/api/team-tryouts/team/${teamId}`);
  }

  async createTeamTryout(tryoutData) {
    return await this.request(`/api/team-tryouts/`, {
      method: 'POST',
      body: JSON.stringify(tryoutData)
    });
  }

  async updateTeamTryout(tryoutId, tryoutData) {
    return await this.request(`/api/team-tryouts/${tryoutId}`, {
      method: 'PUT',
      body: JSON.stringify(tryoutData)
    });
  }

  async deleteTeamTryout(tryoutId) {
    return await this.request(`/api/team-tryouts/${tryoutId}`, {
      method: 'DELETE'
    });
  }

  async openTeamTryout(tryoutId) {
    return await this.request(`/api/team-tryouts/${tryoutId}/open`, {
      method: 'POST'
    });
  }

  async closeTeamTryout(tryoutId) {
    return await this.request(`/api/team-tryouts/${tryoutId}/close`, {
      method: 'POST'
    });
  }

  async getTryoutRegistrations(tryoutId) {
    return await this.request(`/api/team-tryouts/${tryoutId}/registrations`);
  }

  async getTeamTryoutRegistrations(teamId) {
    return await this.request(`/api/team-tryouts/team/${teamId}/registrations`);
  }

  // Public registration methods (no auth required)
  async getTeamTryoutByCode(tryoutCode) {
    return await this.request(`/api/team-tryouts/public/${tryoutCode}`, {
      skipAuth: true
    });
  }

  async registerForTeamTryout(tryoutCode, registrationData) {
    return await this.request(`/api/team-tryouts/public/${tryoutCode}/register`, {
      method: 'POST',
      body: JSON.stringify(registrationData),
      skipAuth: true
    });
  }

  // Offer Management API methods
  async createOffer(teamId, offerData) {
    return await this.request(`/api/offers/teams/${teamId}/offers`, {
      method: 'POST',
      body: JSON.stringify(offerData)
    });
  }

  async listTeamOffers(teamId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const endpoint = queryString
      ? `/api/offers/teams/${teamId}/offers?${queryString}`
      : `/api/offers/teams/${teamId}/offers`;

    return await this.request(endpoint);
  }

  async renewOffer(offerId, expiresAt = null) {
    return await this.request(`/api/offers/${offerId}/renew`, {
      method: 'POST',
      body: JSON.stringify({ expires_at: expiresAt })
    });
  }

  async resendOfferRegistrationEmail(offerId) {
    return await this.request(`/api/offers/${offerId}/resend-registration`, {
      method: 'POST'
    });
  }

  async resendOfferAcceptanceEmail(offerId) {
    return await this.request(`/api/offers/${offerId}/resend-acceptance`, {
      method: 'POST'
    });
  }

  async cancelOffer(offerId) {
    return await this.request(`/api/offers/${offerId}`, {
      method: 'DELETE'
    });
  }

  // Offer acceptance flow - user-facing endpoints
  async getOfferByRegistrationToken(registrationToken) {
    return await this.request(`/api/offers/${registrationToken}`);
  }

  async completeOfferRegistration(registrationToken, userId) {
    return await this.request(`/api/offers/${registrationToken}/complete-registration`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    });
  }

  // Legacy aliases for backward compatibility (deprecated)
  async getProgramBatches(programId) {
    return await this.getProgramTeams(programId);
  }

  async createBatch(programId, batchData) {
    return await this.createTeam(programId, batchData);
  }

  async updateBatch(programId, batchId, batchData) {
    return await this.updateTeam(batchId, batchData);
  }

  async deleteBatch(programId, batchId) {
    return await this.deleteTeam(batchId);
  }

  // Club Season Management API methods (Club Admin)
  async getSeasons(filters = {}) {
    const params = new URLSearchParams();

    if (filters.year) params.append('year', filters.year);
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/api/seasons/?${queryString}` : '/api/seasons/';

    return await this.request(endpoint);
  }

  async getSeason(seasonId) {
    return await this.request(`/api/seasons/${seasonId}`);
  }

  async createSeason(seasonData) {
    return await this.request('/api/seasons/', {
      method: 'POST',
      body: JSON.stringify(seasonData)
    });
  }

  async updateSeason(seasonId, seasonData) {
    return await this.request(`/api/seasons/${seasonId}`, {
      method: 'PUT',
      body: JSON.stringify(seasonData)
    });
  }

  async activateSeason(seasonId) {
    return await this.request(`/api/seasons/${seasonId}/activate`, {
      method: 'PATCH'
    });
  }

  async deleteSeason(seasonId) {
    return await this.request(`/api/seasons/${seasonId}`, {
      method: 'DELETE'
    });
  }

  async getCurrentSeason() {
    return await this.request('/api/seasons/current');
  }

  async getDefaultSeason() {
    return await this.request('/api/seasons/default');
  }

  async getSeasonSummary() {
    return await this.request('/api/seasons/summary');
  }

  async getSeasonsForCalendarSelection() {
    return await this.request('/api/seasons/for-calendar-selection');
  }

  async getSeasonTemplates(year) {
    const endpoint = year ? `/api/seasons/templates/?year=${year}` : '/api/seasons/templates/';
    return await this.request(endpoint);
  }

  async getSeasonStats(club_id) {
    const endpoint = club_id ? `/api/seasons/stats/?club_id=${club_id}` : '/api/seasons/stats/';
    return await this.request(endpoint);
  }

  // Calendar Events API methods
  async getCalendarEvents(calendarId, filters = {}) {
    const params = new URLSearchParams();

    if (filters.time_filter) params.append('time_filter', filters.time_filter);
    if (filters.page) params.append('page', filters.page);
    if (filters.page_size) params.append('page_size', filters.page_size);

    const queryString = params.toString();
    const endpoint = queryString ?
      `/api/calendars/${calendarId}/events?${queryString}` :
      `/api/calendars/${calendarId}/events`;

    return await this.request(endpoint);
  }

  async createCalendarEvent(calendarId, eventData) {
    return await this.request(`/api/calendars/${calendarId}/events`, {
      method: 'POST',
      body: JSON.stringify(eventData)
    });
  }

  async updateCalendarEvent(eventId, eventData) {
    return await this.request(`/api/calendars/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData)
    });
  }

  async deleteCalendarEvent(eventId) {
    return await this.request(`/api/calendars/events/${eventId}`, {
      method: 'DELETE'
    });
  }

  async getCalendarEvent(eventId) {
    return await this.request(`/api/calendars/events/${eventId}`);
  }

  // ============================================================
  // SYSTEM TEMPLATE API METHODS
  // ============================================================

  async getSystemTemplates(filters = {}) {
    const params = new URLSearchParams();
    if (filters.template_type) params.append('template_type', filters.template_type);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const queryString = params.toString();
    const endpoint = queryString ? `/api/admin/system-templates?${queryString}` : '/api/admin/system-templates';
    return await this.request(endpoint);
  }

  async getSystemTemplate(templateId) {
    return await this.request(`/api/admin/system-templates/${templateId}`);
  }

  async createSystemTemplate(templateData) {
    return await this.request('/api/admin/system-templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
  }

  async updateSystemTemplate(templateId, templateData) {
    return await this.request(`/api/admin/system-templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(templateData)
    });
  }

  async deleteSystemTemplate(templateId) {
    return await this.request(`/api/admin/system-templates/${templateId}`, {
      method: 'DELETE'
    });
  }

  async activateSystemTemplate(templateId) {
    return await this.request(`/api/admin/system-templates/${templateId}/activate`, {
      method: 'POST'
    });
  }

  async deactivateSystemTemplate(templateId) {
    return await this.request(`/api/admin/system-templates/${templateId}/deactivate`, {
      method: 'POST'
    });
  }

  async getSystemTemplateStats(filters = {}) {
    const params = new URLSearchParams();
    if (filters.template_type) params.append('template_type', filters.template_type);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);

    const queryString = params.toString();
    const endpoint = queryString ? `/api/admin/system-templates/stats/count?${queryString}` : '/api/admin/system-templates/stats/count';
    return await this.request(endpoint);
  }

  async getTemplatesForClubCreation(clubType, sportType = null) {
    const params = new URLSearchParams({ club_type: clubType });
    if (sportType) params.append('sport_type', sportType);
    return await this.request(`/api/admin/system-templates/for-club-creation?${params.toString()}`);
  }

  // ============================================================
  // PLATFORM ADMIN USER API METHODS
  // ============================================================

  async getPlatformAdmins() {
    return await this.request('/api/admin/platform-admins');
  }

  async createPlatformAdmin(adminData) {
    return await this.request('/api/admin/platform-admins', {
      method: 'POST',
      body: JSON.stringify(adminData)
    });
  }

  async assignPlatformAdminRole(userId, role = 'platform_admin') {
    return await this.request('/api/admin/platform-admins/assign-role', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role })
    });
  }

  async revokePlatformAdmin(userId) {
    return await this.request(`/api/admin/platform-admins/${userId}`, {
      method: 'DELETE'
    });
  }

  async getPlatformAdminStats() {
    return await this.request('/api/admin/platform-admins/stats/count');
  }

  // Generic HTTP methods for convenience
  async get(endpoint) {
    return await this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(endpoint, data) {
    return await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint) {
    return await this.request(endpoint, { method: 'DELETE' });
  }

  // ============================================================
  // SESSION TEMPLATE AND TRAINING SESSION API METHODS
  // ============================================================

  /**
   * Get activity templates for a branch (includes club and branch templates)
   */
  async getBranchActivityTemplates(branchId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.activity_type !== undefined) params.append('activity_type', filters.activity_type);
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/api/branches/${branchId}/activity-templates?${queryString}`
      : `/api/branches/${branchId}/activity-templates`;

    return await this.request(endpoint);
  }

  /**
   * Get activity templates for a club
   */
  async getClubActivityTemplates(clubId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.activity_type !== undefined) params.append('activity_type', filters.activity_type);
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/api/clubs/${clubId}/activity-templates?${queryString}`
      : `/api/clubs/${clubId}/activity-templates`;

    return await this.request(endpoint);
  }

  /**
   * Get session templates for a branch (includes platform, club, and branch templates)
   */
  async getBranchSessionTemplates(branchId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);
    if (filters.include_activities !== undefined) params.append('include_activities', filters.include_activities);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/api/branches/${branchId}/session-templates?${queryString}`
      : `/api/branches/${branchId}/session-templates`;

    return await this.request(endpoint);
  }

  /**
   * Get session templates for a club (includes platform and club templates)
   */
  async getClubSessionTemplates(clubId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);
    if (filters.include_activities !== undefined) params.append('include_activities', filters.include_activities);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/api/clubs/${clubId}/session-templates?${queryString}`
      : `/api/clubs/${clubId}/session-templates`;

    return await this.request(endpoint);
  }

  /**
   * Get a specific session template with activities
   */
  async getSessionTemplate(templateId, includeActivities = true) {
    const params = new URLSearchParams();
    params.append('include_activities', includeActivities);

    return await this.request(`/api/session-templates/${templateId}?${params.toString()}`);
  }

  /**
   * Create a training session from a template (linked to an event)
   */
  async createTrainingSession(branchId, sessionData) {
    return await this.request(`/api/branches/${branchId}/training-sessions`, {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
  }

  /**
   * Get training session details
   */
  async getTrainingSession(sessionId) {
    return await this.request(`/api/training-sessions/${sessionId}`);
  }

  /**
   * Get training session linked to an event
   */
  async getEventTrainingSession(eventId) {
    return await this.request(`/api/events/${eventId}/training-session`);
  }

  /**
   * Update a training session
   */
  async updateTrainingSession(sessionId, sessionData) {
    return await this.request(`/api/training-sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(sessionData)
    });
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export both the class and the instance
export { ApiService };
export default apiService;
